import React, { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Inbox, Send, Archive, ArchiveRestore, Trash2, ArrowLeft, Loader2, MailOpen, Search, X, User } from "lucide-react";
import { Link } from "react-router-dom";
import EmojiPicker from "@/components/EmojiPicker";
import ImageUploadButton, { ImageThumbPreview, ChatImage } from "@/components/ImageUploadButton";
import VideoEmbed from "@/components/VideoEmbed";

const TR = {
  sk: {
    heading: "Schránka správ", empty: "Žiadne správy. Klikni na meno užívateľa v komunite a napíš mu prvý.",
    activeTab: "Doručené", archivedTab: "Archív", archivedEmpty: "Žiadne archivované konverzácie.",
    selectHint: "Vyber konverzáciu vľavo →",
    archive: "Archivovať", unarchive: "Vrátiť do doručených", del: "Zmazať", delConfirm: "Naozaj zmazať celú konverzáciu? Toto sa nedá vrátiť.",
    placeholder: "Napíš odpoveď…", send: "Odoslať",
    searchPh: "Hľadať podľa mena…", searchEmpty: "Žiadna konverzácia neobsahuje",
  },
  en: {
    heading: "Inbox", empty: "No messages. Click a user's name in the community and message them first.",
    activeTab: "Inbox", archivedTab: "Archive", archivedEmpty: "No archived conversations.",
    selectHint: "Select a conversation on the left →",
    archive: "Archive", unarchive: "Move to inbox", del: "Delete", delConfirm: "Really delete the entire conversation? This cannot be undone.",
    placeholder: "Type a reply…", send: "Send",
    searchPh: "Search by name…", searchEmpty: "No conversation matches",
  },
  cs: {
    heading: "Schránka zpráv", empty: "Žádné zprávy. Klikni na jméno uživatele v komunitě a napiš mu první.",
    activeTab: "Doručené", archivedTab: "Archiv", archivedEmpty: "Žádné archivované konverzace.",
    selectHint: "Vyber konverzaci vlevo →",
    archive: "Archivovat", unarchive: "Vrátit do doručených", del: "Smazat", delConfirm: "Opravdu smazat celou konverzaci? Toto nelze vrátit.",
    placeholder: "Napiš odpověď…", send: "Odeslat",
    searchPh: "Hledat podle jména…", searchEmpty: "Žádná konverzace neodpovídá",
  },
  de: {
    heading: "Posteingang", empty: "Keine Nachrichten. Klicke auf einen Namen in der Community und schreib zuerst.",
    activeTab: "Posteingang", archivedTab: "Archiv", archivedEmpty: "Keine archivierten Konversationen.",
    selectHint: "Wähle links eine Konversation →",
    archive: "Archivieren", unarchive: "Wiederherstellen", del: "Löschen", delConfirm: "Konversation wirklich löschen? Dies kann nicht rückgängig gemacht werden.",
    placeholder: "Antwort schreiben…", send: "Senden",
    searchPh: "Nach Name suchen…", searchEmpty: "Keine passende Konversation",
  },
  pl: {
    heading: "Skrzynka wiadomości", empty: "Brak wiadomości. Kliknij imię użytkownika w społeczności i napisz jako pierwszy.",
    activeTab: "Odebrane", archivedTab: "Archiwum", archivedEmpty: "Brak zarchiwizowanych konwersacji.",
    selectHint: "Wybierz konwersację po lewej →",
    archive: "Archiwizuj", unarchive: "Cofnij do odebranych", del: "Usuń", delConfirm: "Na pewno usunąć całą konwersację? Tego nie można cofnąć.",
    placeholder: "Napisz odpowiedź…", send: "Wyślij",
    searchPh: "Szukaj po imieniu…", searchEmpty: "Brak pasujących konwersacji",
  },
  fr: {
    heading: "Boîte de messagerie", empty: "Aucun message. Clique sur le nom d'un utilisateur dans la communauté et écris-lui en premier.",
    activeTab: "Reçus", archivedTab: "Archive", archivedEmpty: "Aucune conversation archivée.",
    selectHint: "Sélectionne une conversation à gauche →",
    archive: "Archiver", unarchive: "Remettre dans les reçus", del: "Supprimer", delConfirm: "Vraiment supprimer toute la conversation ? Cette action est irréversible.",
    placeholder: "Écris ta réponse…", send: "Envoyer",
    searchPh: "Rechercher par nom…", searchEmpty: "Aucune conversation ne contient",
  },
};

export default function MessageInbox({ initialPartnerId = null }) {
  const { user } = useAuth();
  const { lang } = useLang();
  const tr = TR[lang] || TR.sk;
  const [tab, setTab] = useState("active"); // active | archived
  const [convs, setConvs] = useState([]);
  const [selected, setSelected] = useState(initialPartnerId);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [dmImage, setDmImage] = useState(null);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const scrollRef = useRef(null);

  const loadConvs = useCallback(() => {
    api.get("/dm/conversations").then((r) => {
      setConvs(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const loadMessages = useCallback((partnerId) => {
    api.get(`/dm/conversation/${partnerId}`).then((r) => {
      setMessages(r.data);
      api.post(`/dm/mark-read/${partnerId}`).then(() => {
        loadConvs();
        try { window.dispatchEvent(new CustomEvent("dm:read")); } catch { /* ignore */ }
      });
    }).catch(() => {});
  }, [loadConvs]);

  useEffect(() => { loadConvs(); }, [loadConvs]);
  useEffect(() => { if (selected) loadMessages(selected); }, [selected, loadMessages]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }); }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    if ((!text.trim() && !dmImage) || !selected) return;
    setSending(true);
    try {
      await api.post("/dm/send", { to_user_id: selected, text: text.trim(), image_base64: dmImage });
      setText("");
      setDmImage(null);
      loadMessages(selected);
    } catch (e2) { /* ignore */ } finally { setSending(false); }
  };

  const toggleArchive = async (partnerId) => {
    await api.post(`/dm/archive/${partnerId}`);
    loadConvs();
  };

  const deleteConv = async (partnerId) => {
    if (!window.confirm(tr.delConfirm)) return;
    await api.delete(`/dm/conversation/${partnerId}`);
    if (selected === partnerId) { setSelected(null); setMessages([]); }
    loadConvs();
  };

  const filtered = convs
    .filter((c) => tab === "active" ? !c.archived : c.archived)
    .filter((c) => {
      if (!search.trim()) return true;
      const q = search.trim().toLowerCase();
      return (c.partner_name || "").toLowerCase().includes(q);
    });
  const activeConv = convs.find((c) => c.partner_id === selected);

  if (loading) return <div className="border border-zinc-800 bg-[var(--wb-surface)] p-6 mb-6 text-center text-sm text-zinc-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> ...</div>;
  if (convs.length === 0) {
    return (
      <div className="border border-zinc-800 bg-[var(--wb-surface)] p-6 mb-6" data-testid="inbox-empty">
        <h2 className="font-display font-bold text-lg mb-2 flex items-center gap-2"><Inbox className="w-4 h-4 text-gold-500" /> {tr.heading}</h2>
        <p className="text-sm text-zinc-500">{tr.empty}</p>
      </div>
    );
  }

  return (
    <div className="border border-zinc-800 bg-[var(--wb-surface)] mb-6" data-testid="message-inbox">
      <div className="p-4 border-b border-zinc-800 flex items-center justify-between gap-2 flex-wrap">
        <h2 className="font-display font-bold text-lg flex items-center gap-2">
          <Inbox className="w-4 h-4 text-gold-500" /> {tr.heading}
        </h2>
        <div className="flex gap-1">
          <button data-testid="inbox-tab-active" onClick={() => { setTab("active"); setSelected(null); }}
            className={`px-3 py-1 text-xs font-mono uppercase tracking-wider transition-colors ${tab === "active" ? "bg-gold-500/15 border border-gold-700 text-gold-400" : "text-zinc-500 hover:text-zinc-200"}`}>
            {tr.activeTab}
          </button>
          <button data-testid="inbox-tab-archived" onClick={() => { setTab("archived"); setSelected(null); }}
            className={`px-3 py-1 text-xs font-mono uppercase tracking-wider transition-colors ${tab === "archived" ? "bg-gold-500/15 border border-gold-700 text-gold-400" : "text-zinc-500 hover:text-zinc-200"}`}>
            {tr.archivedTab}
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-[280px_1fr] h-[calc(100dvh-220px)] min-h-[420px] max-h-[700px] overflow-hidden">
        {/* Sidebar */}
        <div className={`border-r border-zinc-800 flex flex-col min-h-0 ${selected ? "hidden md:flex" : "flex"}`}>
          {convs.length > 5 && (
            <div className="p-2 border-b border-zinc-800 relative flex-shrink-0">
              <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                data-testid="inbox-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={tr.searchPh}
                className="w-full bg-zinc-900 border border-zinc-800 focus:border-gold-700 focus:outline-none text-xs pl-7 pr-7 py-1.5 font-mono"
              />
              {search && (
                <button
                  data-testid="inbox-search-clear"
                  onClick={() => setSearch("")}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
          <div className="flex-1 overflow-y-auto min-h-0">
          {filtered.length === 0 ? (
            <div className="p-4 text-xs text-zinc-600">{tab === "archived" ? tr.archivedEmpty : tr.empty}</div>
          ) : filtered.map((c) => (
            <button key={c.partner_id} onClick={() => setSelected(c.partner_id)}
              data-testid={`inbox-conv-${c.partner_id}`}
              className={`w-full text-left p-3 border-b border-zinc-800 hover:bg-zinc-900/50 transition-colors flex items-start gap-2 ${selected === c.partner_id ? "bg-zinc-900/70" : ""}`}>
              <div className="w-8 h-8 bg-zinc-800 border border-zinc-700 flex-shrink-0 overflow-hidden">
                {c.partner_picture ? <img src={c.partner_picture} alt="" className="w-full h-full object-cover" /> :
                  <div className="w-full h-full flex items-center justify-center text-xs font-display font-bold text-zinc-400 uppercase">{c.partner_name?.[0]}</div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-1">
                  <span className="font-display font-bold text-sm truncate">{c.partner_name}</span>
                  {c.unread > 0 && (
                    <span data-testid={`inbox-conv-${c.partner_id}-unread`}
                      className="min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-red-600 text-white rounded-full inline-flex items-center justify-center">
                      {c.unread > 9 ? "9+" : c.unread}
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-500 truncate mt-0.5">
                  {c.last_from_me && <span className="text-zinc-600">Ty: </span>}{c.last_text}
                </div>
                <div className="text-[10px] text-zinc-600 font-mono mt-0.5">{new Date(c.last_ts).toLocaleString()}</div>
              </div>
            </button>
          ))}
          </div>
        </div>

        {/* Conversation */}
        <div className={`flex flex-col min-h-0 ${selected ? "" : "hidden md:flex"}`}>
          {!activeConv ? (
            <div className="flex-1 flex items-center justify-center text-sm text-zinc-600 p-6">
              <MailOpen className="w-4 h-4 mr-2" /> {tr.selectHint}
            </div>
          ) : (
            <>
              <div className="p-3 border-b border-zinc-800 flex items-center gap-2">
                <button onClick={() => setSelected(null)} className="md:hidden p-1 text-zinc-500"><ArrowLeft className="w-4 h-4" /></button>
                <Link
                  to={`/u/${activeConv.partner_id}`}
                  data-testid={`inbox-${activeConv.partner_id}-profile`}
                  title={`Otvoriť profil — ${activeConv.partner_name}`}
                  className="flex items-center gap-2 hover:text-gold-400 transition-colors group"
                >
                  <div className="w-8 h-8 bg-zinc-800 border border-zinc-700 group-hover:border-gold-500 overflow-hidden transition-colors">
                    {activeConv.partner_picture ? <img src={activeConv.partner_picture} alt="" className="w-full h-full object-cover" /> :
                      <div className="w-full h-full flex items-center justify-center text-xs font-display font-bold text-zinc-400 uppercase">{activeConv.partner_name?.[0]}</div>}
                  </div>
                  <div className="font-display font-bold text-sm group-hover:text-gold-300">{activeConv.partner_name}</div>
                  <User className="w-3 h-3 text-zinc-600 group-hover:text-gold-400" />
                </Link>
                <div className="flex-1" />
                <button data-testid={`inbox-${activeConv.partner_id}-archive`} onClick={() => toggleArchive(activeConv.partner_id)}
                  title={activeConv.archived ? tr.unarchive : tr.archive}
                  className="p-1.5 text-zinc-500 hover:text-gold-400">
                  {activeConv.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                </button>
                <button data-testid={`inbox-${activeConv.partner_id}-delete`} onClick={() => deleteConv(activeConv.partner_id)}
                  title={tr.del}
                  className="p-1.5 text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0" data-testid="inbox-messages">
                {messages.map((m) => {
                  const fromMe = m.from_user_id === user.id;
                  const localized = (m.text_i18n && (m.text_i18n[lang] || m.text_i18n[m.source_lang])) || m.text;
                  return (
                    <div key={m.id} className={`flex ${fromMe ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] p-2.5 ${fromMe ? "bg-gold-500/20 border border-gold-700/40 text-white" : "bg-zinc-900 border border-zinc-800 text-zinc-200"}`}>
                        {localized && <div className="text-sm whitespace-pre-wrap break-words">{localized}</div>}
                        {m.image && <ChatImage src={m.image} />}
                        {m.video_url && <VideoEmbed url={m.video_url} />}
                        <div className="text-[10px] text-zinc-500 font-mono mt-1">{new Date(m.ts).toLocaleString()}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <form onSubmit={send} className="p-3 border-t border-zinc-800 flex items-center gap-2" data-testid="inbox-reply-form">
                {dmImage && (
                  <div className="absolute bottom-full left-3 mb-2 z-10">
                    <ImageThumbPreview src={dmImage} onClear={() => setDmImage(null)} />
                  </div>
                )}
                <ImageUploadButton onPicked={setDmImage} testid="inbox-image-btn" />
                <input data-testid="inbox-reply-input" type="text" value={text} onChange={(e) => setText(e.target.value)}
                  placeholder={tr.placeholder} className="wb-input flex-1 px-3 py-2 text-sm" />
                <EmojiPicker testid="inbox-emoji" onPick={(e) => setText(text + e)} />
                <button type="submit" disabled={sending || (!text.trim() && !dmImage)} data-testid="inbox-reply-send"
                  className="wb-btn-primary px-4 py-2 text-sm inline-flex items-center gap-1.5">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  <span className="hidden md:inline">{tr.send}</span>
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
